import React, { useState } from 'react';
// Assuming Header and Footer components exist in '../components/'
// Uncomment these lines if you have Header and Footer components
// import Header from '../components/Header';
// import Footer from '../components/Footer';

// NATO Phonetic Alphabet including numbers
const phonetic = {
    A: 'Alpha', B: 'Bravo', C: 'Charlie', D: 'Delta', E: 'Echo', F: 'Foxtrot',
    G: 'Golf', H: 'Hotel', I: 'India', J: 'Juliett', K: 'Kilo', L: 'Lima',
    M: 'Mike', N: 'November', O: 'Oscar', P: 'Papa', Q: 'Quebec', R: 'Romeo',
    S: 'Sierra', T: 'Tango', U: 'Uniform', V: 'Victor', W: 'Whiskey', X: 'X-ray',
    Y: 'Yankee', Z: 'Zulu',
    '0': 'Zero', '1': 'One', '2': 'Two', '3': 'Three', '4': 'Four',
    '5': 'Five', '6': 'Six', '7': 'Seven', '8': 'Eight', '9': 'Nine'
  };

const AviationPilotATCUnit1 = () => {
  // State for each exercise
  const [startChoice, setStartChoice] = useState('');
  const [startFeedback, setStartFeedback] = useState('');
  const [dictationInput, setDictationInput] = useState('');
  const [dictationFeedback, setDictationFeedback] = useState('');
  const [grammarInput, setGrammarInput] = useState('');
  const [grammarFeedback, setGrammarFeedback] = useState('');
  const [dialogueInput, setDialogueInput] = useState('');
  const [dialogueFeedback, setDialogueFeedback] = useState('');
  const [callsign, setCallsign] = useState('');
  const [spelled, setSpelled] = useState('');
  const [readbackChoice, setReadbackChoice] = useState('');
  const [readbackFeedback, setReadbackFeedback] = useState('');
  const [takeoffInput, setTakeoffInput] = useState('');
  const [takeoffFeedback, setTakeoffFeedback] = useState('');
  const [sidDef, setSidDef] = useState('');
  const [starDef, setStarDef] = useState('');
  const [sidstarFeedback, setSidstarFeedback] = useState('');
  // State for fill-in-the-blanks (Exercise 10)
  const [phrase1, setPhrase1] = useState('');
  const [phrase2, setPhrase2] = useState('');
  const [phrase3, setPhrase3] = useState('');
  const [phrase4, setPhrase4] = useState('');
  const [phrasesFeedback, setPhrasesFeedback] = useState('');


  // --- Event Handlers ---

  const handleCheckStart = () => {
    if (startChoice === 'pushback') {
      setStartFeedback("✅ Correct! Always start with requesting pushback.");
    } else {
      setStartFeedback("❌ Not quite. The correct first step after confirming readiness is to request pushback.");
    }
  };

  const handleSpellCallsign = () => {
    const upperCallsign = callsign.toUpperCase();
    // Spell out letters and numbers using the phonetic map
    const spelling = upperCallsign
      .split('')
      .map(char => phonetic[char] || char) // Use phonetic word or the character itself
      .join(' ');
    setSpelled(spelling);
  };

  const handleSubmitDictation = () => {
    // In a real app, compare dictationInput to the correct text
    setDictationFeedback("✅ Submitted! Your instructor will review your dictation. (Check model answer for self-assessment).");
  };

  const handleSubmitGrammar = () => {
    // Provide feedback; more robust checking could be added
    setGrammarFeedback("✅ Submitted! Check model answer for comparison.");
  };

   const handleSubmitDialogue = () => {
    // Provide feedback
    setDialogueFeedback("✅ Well done! Your dialogue response has been submitted for review. (Check model answer for comparison).");
  };

  const handleCheckReadback = () => {
    if (readbackChoice === 'correct') {
      setReadbackFeedback("✅ Correct! Always read back ATC instructions precisely as given.");
    } else {
      setReadbackFeedback("❌ Incorrect. Exact phraseology is crucial for safety. The correct readback repeats the instruction.");
    }
  };

  const handleCheckTakeoff = () => {
     // Check if the input contains key elements (case-insensitive)
     const lowerInput = takeoffInput.toLowerCase();
     if (lowerInput.includes("runway 27") && lowerInput.includes("cleared for take-off") && lowerInput.includes("speedbird 245")) {
         setTakeoffFeedback("✅ Correct! Readback: 'Runway 27, cleared for take-off, Speedbird 245.' Then apply thrust.");
     } else {
         setTakeoffFeedback("❌ Almost! Ensure your readback includes the runway, the clearance phrase, and your callsign exactly.");
     }
  };

  const handleCheckSIDSTAR = () => {
    if (sidDef === 'departure' && starDef === 'arrival') {
      setSidstarFeedback("✅ All correct! SID = Standard Instrument Departure, STAR = Standard Terminal Arrival Route.");
    } else {
        let feedbackText = "❌ Check again: ";
        if (sidDef !== 'departure') feedbackText += "SID definition is incorrect. ";
        if (starDef !== 'arrival') feedbackText += "STAR definition is incorrect.";
      setSidstarFeedback(feedbackText);
    }
  };

  const handleCheckPhrases = () => {
    // Check the selected values for Exercise 10
    const correct = phrase1 === 'WILCO' && phrase2 === 'AFFIRM' && phrase3 === 'NEGATIVE' && phrase4 === 'ROGER';
    if (correct) {
        setPhrasesFeedback("✅ All correct! WILCO (Will Comply), AFFIRM (Yes), NEGATIVE (No), ROGER (Received).");
    } else {
        setPhrasesFeedback("❌ Not quite right. Review the definitions: WILCO=Will Comply, AFFIRM=Yes, NEGATIVE=No, ROGER=Received.");
    }
  };

  // --- Helper Functions ---

  const showModelAnswer = (type) => {
    const answers = {
      grammar: "I check the flight plan. We request pushback. ATC clears us. I hold short of Runway 27.",
      dialogue: "Pilot: 'Ground, Speedbird 245, Gate B12, request pushback.'\nGround: 'Speedbird 245, roger. Cleared for pushback. Contact Tower on 118.1 after.'\nPilot: 'Cleared for pushback, Speedbird 245. Switching to 118.1.'",
      dictation: "ATIS Charlie, Toronto International, one four three zero Zulu. Wind two seven zero at one zero knots, visibility one zero kilometers, ceiling two thousand feet broken, temperature one five, dewpoint one zero. Altimeter two niner niner two. ILS Runway Two Four Left approach in use. Advise controller on initial contact you have information Charlie.",
      takeoff: "Pilot: 'Runway 27, cleared for take-off, Speedbird 245.' (Then advance throttles)."
    };
    // Using a simple alert. Replace with a modal component for better UX.
    alert(`Model Answer:\n\n${answers[type] || 'No model answer available for this section.'}`);
  };

//   const openLexicon = () => {
//     // If Lexicon is an external site:
//     window.open('https://aulice-lexicon.netlify.app', '_blank', 'noopener,noreferrer');
//     // If Lexicon is part of your React app, use Link from react-router-dom
//   };

  const addToLexicon = (word) => {
    // Placeholder function - replace with actual logic to save to state/backend
    console.log(`Adding "${word}" to Lexicon...`);
    // Use a more integrated notification system instead of alert if possible
    alert(`✅ "${word}" added to your Lexicon (Simulation).`);
  };


  return (
    // If Header and Footer components exist, uncomment and place them appropriately
    // <Header />
    <div className="font-sans bg-[#f7f9fc] text-[#2c3e50] leading-relaxed min-h-screen">
      <header className="text-center py-10 px-5 bg-[#0056b3] text-white shadow-md">
        <h1 className="text-2xl md:text-3xl font-bold">✈️ Unit 1: Aviation English</h1>
        <p className="mt-2 text-sm md:text-base">Pre-Flight & Ground Operations | ICAO Radiotelephony & ATC Procedures</p>
      </header>

      <main className="max-w-4xl mx-auto py-10 px-5">
        <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg space-y-8">

          {/* Exercise 1: Starting Point */}
          <section className="border-b pb-6">
            <h2 className="text-xl font-bold text-[#0056b3] mb-3">🔥 Starting Point: What Do You Say First?</h2>
            <p className="mb-3 text-sm">You are the pilot. The flight plan is filed. The ATIS is received. Ground Control is calling. What is the correct first phrase?</p>
            <select
              value={startChoice}
              onChange={(e) => setStartChoice(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded mb-3 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">-- Choose --</option>
              <option value="pushback">Request pushback</option>
              <option value="takeoff">Cleared for takeoff</option>
              <option value="tower">Contact Tower on 118.1</option>
              <option value="hold">Hold short of Runway 27</option>
            </select>
            <button onClick={handleCheckStart} className="px-4 py-2 bg-[#0056b3] text-white rounded mr-2 hover:bg-[#004a99] transition text-sm">
              ✅ Check Answer
            </button>
            {startFeedback && <div className="mt-2 p-2 bg-[#e8f4f8] rounded text-sm text-[#0056b3]">{startFeedback}</div>}
          </section>

          {/* Example Dialogue & Vocab */}
           <section className="p-5 bg-gray-50 border rounded-lg border-b pb-6">
             <h2 className="text-lg font-semibold text-[#0056b3] mb-3">📌 Example: Pilot-Ground Control Dialogue</h2>
             <div className="space-y-1 text-gray-700 text-sm mb-4 bg-white p-3 border rounded">
                 <p><strong>Pilot:</strong> "Ground, Speedbird 245, Gate B12, request pushback."</p>
                 <p><strong>Ground:</strong> "Speedbird 245, roger. You are cleared for pushback. After pushback, contact Tower on 118.1."</p>
                 <p><strong>Pilot:</strong> "Cleared for pushback, Speedbird 245. Switching to 118.1."</p>
             </div>
             <p className="text-xs text-gray-500 italic mb-4">ATIS: Automatic Terminal Information Service — continuous broadcast of weather and runway info.</p>

             <h3 className="text-md font-semibold text-[#0056b3] mb-2">Key Vocabulary:</h3>
             <div className="flex flex-wrap gap-2">
                {['clearance', 'pushback', 'taxi', 'hold short', 'line up and wait', 'ATIS', 'roger', 'squawk'].map(word => (
                <button
                    key={word}
                    onClick={() => addToLexicon(word)}
                    className="bg-[#e8f4f8] px-3 py-1 rounded-full text-xs font-medium text-[#0056b3] cursor-pointer hover:bg-[#d0e8f0] hover:shadow-sm transition"
                    title={`Add "${word}" to Lexicon`}
                >
                    {word} <span className="text-blue-400 ml-1">+</span> {/* Indicate add action */}
                </button>
                ))}
            </div>
          </section>

          {/* Exercise 3: ATIS Dictation */}
          <section className="p-5 bg-[#f8f9fa] border-l-4 border-[#0056b3] rounded border-b pb-6">
            <h3 className="font-bold text-lg text-[#0056b3] mb-2">📝 Exercise 3: ATIS Dictation</h3>
            <p className="mb-3 text-sm">Listen to the ATIS message and write it down exactly.</p>
            <audio controls className="w-full mb-3" aria-label="ATIS Dictation Audio">
              {/* IMPORTANT: Replace with a valid URL or path to your audio file */}
              {/* <source src="/audio/atis-example.mp3" type="audio/mpeg" /> */}
               Your browser does not support the audio element. (Audio source missing)
            </audio>
            <textarea
              value={dictationInput}
              onChange={(e) => setDictationInput(e.target.value)}
              placeholder="Write what you hear..."
              className="w-full p-3 border border-gray-300 rounded mb-3 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
            />
            <div className="flex flex-wrap gap-2">
              <button onClick={handleSubmitDictation} className="px-4 py-2 bg-[#0056b3] text-white rounded text-sm hover:bg-[#004a99] transition">
                ✅ Submit
              </button>
              <button onClick={() => showModelAnswer('dictation')} className="px-4 py-2 bg-gray-200 text-gray-800 rounded text-sm hover:bg-gray-300 transition">
                🔍 Show Model Answer
              </button>
            </div>
            {dictationFeedback && <div className="mt-2 p-2 bg-[#e8f4f8] rounded text-sm text-[#0056b3]">{dictationFeedback}</div>}
          </section>

          {/* Exercise 4: Aviation Alphabet */}
          <section className="p-5 bg-[#f8f9fa] border-l-4 border-[#0056b3] rounded border-b pb-6">
            <h3 className="font-bold text-lg text-[#0056b3] mb-3">📝 Exercise 4: Aviation Alphabet (NATO Phonetic)</h3>
            <div className="max-h-60 overflow-y-auto mb-4 border rounded p-2 text-xs bg-white">
                <table className="w-full border-collapse">
                <thead className="sticky top-0 bg-gray-100">
                    <tr>
                    <th className="text-left py-1 px-2 font-semibold border-b">Letter/Num</th>
                    <th className="text-left py-1 px-2 font-semibold border-b">Word</th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(phonetic).map(([letter, word]) => (
                    <tr key={letter} className="border-b hover:bg-gray-50">
                        <td className="py-1 px-2 font-mono">{letter}</td>
                        <td className="py-1 px-2">{word}</td>
                    </tr>
                    ))}
                </tbody>
                </table>
            </div>
            <label htmlFor="callsignInput" className="block mb-2 text-sm font-medium">Now, spell your call sign:</label>
            <div className="flex items-center gap-2 mb-2">
                <input
                    id="callsignInput"
                    type="text"
                    value={callsign}
                    onChange={(e) => setCallsign(e.target.value)}
                    placeholder="e.g., BAW245"
                    className="w-40 p-1 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    aria-label="Enter callsign"
                />
                 <button onClick={handleSpellCallsign} className="px-4 py-1 bg-[#0056b3] text-white rounded text-sm hover:bg-[#004a99] transition">
                    Spell It
                 </button>
            </div>
             {spelled && <p className="text-sm mt-2 p-2 bg-indigo-50 rounded">Spelled: <span className="font-semibold text-indigo-700">{spelled}</span></p>}
          </section>

          {/* Exercise 5: Find and Correct Errors */}
          <section className="p-5 bg-[#f8f9fa] border-l-4 border-[#0056b3] rounded border-b pb-6">
            <h3 className="font-bold text-lg text-[#0056b3] mb-2">📝 Exercise 5: Find and Correct the Errors</h3>
            <p className="mb-3 text-sm">Correct the grammatical errors in this pilot's report:</p>
            <blockquote className="mb-3 italic text-gray-600 text-sm border-l-2 pl-2 border-gray-300">"I checks the flight plan. We requests pushback. ATC clears us. I holds short of Runway 27."</blockquote>
            <textarea
              value={grammarInput}
              onChange={(e) => setGrammarInput(e.target.value)}
              placeholder="Enter corrected version..."
              className="w-full p-3 border border-gray-300 rounded mb-3 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
            />
            <div className="flex flex-wrap gap-2">
              <button onClick={handleSubmitGrammar} className="px-4 py-2 bg-[#0056b3] text-white rounded text-sm hover:bg-[#004a99] transition">
                ✅ Submit Correction
              </button>
              <button onClick={() => showModelAnswer('grammar')} className="px-4 py-2 bg-gray-200 text-gray-800 rounded text-sm hover:bg-gray-300 transition">
                🔍 Show Model Answer
              </button>
            </div>
            {grammarFeedback && <div className="mt-2 p-2 bg-[#e8f4f8] rounded text-sm text-[#0056b3]">{grammarFeedback}</div>}
          </section>

          {/* Exercise 6: Write Dialogue */}
          <section className="p-5 bg-[#f8f9fa] border-l-4 border-[#0056b3] rounded border-b pb-6">
            <h3 className="font-bold text-lg text-[#0056b3] mb-2">📝 Exercise 6: Write the ATC Dialogue</h3>
             <p className="mb-3 text-sm">Write a short dialogue: Pilot (AirLink 789, Gate C3) requests taxi clearance. Ground Control instructs taxi to Runway 31 via taxiway Alpha, hold short of Runway 25.</p>
            <textarea
              value={dialogueInput}
              onChange={(e) => setDialogueInput(e.target.value)}
              placeholder="Pilot: Ground, AirLink 789...\nGround: AirLink 789, Ground..."
              className="w-full p-3 border border-gray-300 rounded mb-3 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="4"
            />
            <div className="flex flex-wrap gap-2">
              <button onClick={handleSubmitDialogue} className="px-4 py-2 bg-[#0056b3] text-white rounded text-sm hover:bg-[#004a99] transition">
                ✅ Submit Dialogue
              </button>
              <button onClick={() => showModelAnswer('dialogue')} className="px-4 py-2 bg-gray-200 text-gray-800 rounded text-sm hover:bg-gray-300 transition">
                🔍 Show Example Dialogue
              </button>
            </div>
            {dialogueFeedback && <div className="mt-2 p-2 bg-[#e8f4f8] rounded text-sm text-[#0056b3]">{dialogueFeedback}</div>}
          </section>

          {/* Section: Take-Off & Departure */}
          <h2 className="text-xl font-bold text-[#0056b3] mt-8 mb-4 pt-4 border-t">🛫 Take-Off & Departure</h2>

          {/* Exercise 7: Standard Departure Phraseology */}
          <section className="p-5 bg-[#f8f9fa] border-l-4 border-[#0056b3] rounded border-b pb-6">
            <h3 className="font-bold text-lg text-[#0056b3] mb-2">📝 Exercise 7: Standard Departure Phraseology</h3>
            <p className="mb-3 text-sm">You are holding short of Runway 27. The Tower calls and says "Speedbird 245, line up and wait." What is your correct readback?</p>
            <select
              value={readbackChoice}
              onChange={(e) => setReadbackChoice(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded mb-3 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">-- Choose --</option>
              <option value="correct">Line up and wait, Runway 27, Speedbird 245</option>
              <option value="wrong">Ready for departure, Runway 27, Speedbird 245</option>
              <option value="hold">Holding short, Speedbird 245</option>
            </select>
            <button onClick={handleCheckReadback} className="px-4 py-2 bg-[#0056b3] text-white rounded text-sm hover:bg-[#004a99] transition">
              ✅ Check Answer
            </button>
            {readbackFeedback && <div className="mt-2 p-2 bg-[#e8f4f8] rounded text-sm text-[#0056b3]">{readbackFeedback}</div>}
          </section>

          {/* Exercise 8: Take-Off Clearance */}
          <section className="p-5 bg-[#f8f9fa] border-l-4 border-[#0056b3] rounded border-b pb-6">
            <h3 className="font-bold text-lg text-[#0056b3] mb-2">📝 Exercise 8: Take-Off Clearance</h3>
            <p className="mb-3 text-sm">You are lined up on Runway 27. Tower calls: "Speedbird 245, Runway 27, cleared for take-off." What is your readback and what immediate action do you take?</p>
            <textarea
              value={takeoffInput}
              onChange={(e) => setTakeoffInput(e.target.value)}
              placeholder="Pilot Readback: ... Action: ..."
              className="w-full p-3 border border-gray-300 rounded mb-3 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="2"
            />
            <div className="flex flex-wrap gap-2">
              <button onClick={handleCheckTakeoff} className="px-4 py-2 bg-[#0056b3] text-white rounded text-sm hover:bg-[#004a99] transition">
                ✅ Check Answer
              </button>
              <button onClick={() => showModelAnswer('takeoff')} className="px-4 py-2 bg-gray-200 text-gray-800 rounded text-sm hover:bg-gray-300 transition">
                🔍 Show Model Answer
              </button>
            </div>
            {takeoffFeedback && <div className="mt-2 p-2 bg-[#e8f4f8] rounded text-sm text-[#0056b3]">{takeoffFeedback}</div>}
          </section>

          {/* Exercise 9: SID vs. STAR */}
          <section className="p-5 bg-[#f8f9fa] border-l-4 border-[#0056b3] rounded border-b pb-6">
            <h3 className="font-bold text-lg text-[#0056b3] mb-2">📝 Exercise 9: SID vs. STAR</h3>
            <p className="mb-3 text-sm">Match the term to its correct definition.</p>
            <div className="space-y-3 mb-3 text-sm">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <label htmlFor="sidDef" className="w-12 font-semibold">SID:</label>
                    <select
                      id="sidDef"
                      value={sidDef}
                      onChange={(e) => setSidDef(e.target.value)}
                      className="flex-grow p-1 border border-gray-300 rounded bg-white text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">-- Choose Definition --</option>
                      <option value="arrival">A published route for aircraft arriving at an airport.</option>
                      <option value="departure">A published route for aircraft departing an airport.</option>
                    </select>
                </div>
                 <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2 sm:mt-0">
                    <label htmlFor="starDef" className="w-12 font-semibold">STAR:</label>
                    <select
                      id="starDef"
                      value={starDef}
                      onChange={(e) => setStarDef(e.target.value)}
                      className="flex-grow p-1 border border-gray-300 rounded bg-white text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">-- Choose Definition --</option>
                      <option value="arrival">A published route for aircraft arriving at an airport.</option>
                      <option value="departure">A published route for aircraft departing an airport.</option>
                    </select>
                </div>
            </div>
            <button onClick={handleCheckSIDSTAR} className="px-4 py-2 bg-[#0056b3] text-white rounded text-sm hover:bg-[#004a99] transition">
              ✅ Check Answer
            </button>
            {sidstarFeedback && <div className="mt-2 p-2 bg-[#e8f4f8] rounded text-sm text-[#0056b3]">{sidstarFeedback}</div>}
          </section>

          {/* Exercise 10: ICAO Standard Phrases */}
          <section className="p-5 bg-[#f8f9fa] border-l-4 border-[#0056b3] rounded border-b pb-6">
            <h3 className="font-bold text-lg text-[#0056b3] mb-2">📝 Exercise 10: ICAO Standard Phrases</h3>
            <p className="mb-3 text-sm">Fill in the blanks with the correct standard phrase: WILCO, AFFIRM, NEGATIVE, ROGER.</p>
            <ol className="list-decimal pl-5 space-y-4 mb-3 text-sm">
              <li>
                ATC: "Turn left heading three zero zero."<br />
                Pilot: "Left heading three zero zero, Speedbird 245. "
                 <select value={phrase1} onChange={(e) => setPhrase1(e.target.value)} className="ml-1 p-1 border border-gray-300 rounded text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                     <option value="">?</option><option value="WILCO">WILCO</option><option value="AFFIRM">AFFIRM</option><option value="NEGATIVE">NEGATIVE</option><option value="ROGER">ROGER</option>
                 </select>
              </li>
              <li>
                ATC: "Confirm you can accept Flight Level three eight zero?"<br />
                Pilot: "ATC, "
                 <select value={phrase2} onChange={(e) => setPhrase2(e.target.value)} className="ml-1 p-1 border border-gray-300 rounded text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                     <option value="">?</option><option value="WILCO">WILCO</option><option value="AFFIRM">AFFIRM</option><option value="NEGATIVE">NEGATIVE</option><option value="ROGER">ROGER</option>
                 </select> ", we can accept FL380."
              </li>
               <li>
                ATC: "Do you have the runway in sight?"<br />
                Pilot: "ATC, "
                 <select value={phrase3} onChange={(e) => setPhrase3(e.target.value)} className="ml-1 p-1 border border-gray-300 rounded text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                     <option value="">?</option><option value="WILCO">WILCO</option><option value="AFFIRM">AFFIRM</option><option value="NEGATIVE">NEGATIVE</option><option value="ROGER">ROGER</option>
                 </select> ", still IMC." {/* IMC = Instrument Meteorological Conditions */}
              </li>
              <li>
                ATC: "Squawk seven zero zero zero."<br />
                Pilot: "
                 <select value={phrase4} onChange={(e) => setPhrase4(e.target.value)} className="mr-1 p-1 border border-gray-300 rounded text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                     <option value="">?</option><option value="WILCO">WILCO</option><option value="AFFIRM">AFFIRM</option><option value="NEGATIVE">NEGATIVE</option><option value="ROGER">ROGER</option>
                 </select> ", Speedbird 245."
              </li>
            </ol>
            <button onClick={handleCheckPhrases} className="px-4 py-2 bg-[#0056b3] text-white rounded text-sm hover:bg-[#004a99] transition">
              ✅ Check Answers
            </button>
            {phrasesFeedback && <div className="mt-2 p-2 bg-[#e8f4f8] rounded text-sm text-[#0056b3]">{phrasesFeedback}</div>}
          </section>

          {/* Link to Simulator */}
          <section className="mt-8 pt-6 border-t">
            <h2 className="text-lg font-semibold text-[#0056b3] mb-3">🧠 In-Flight Leadership Challenge</h2>
            <p className="mb-3 text-sm">You've mastered the basics. Now, face a complex scenario in our full simulator: Two planes are on a collision course. What’s your move?</p>
            {/* If using react-router-dom, replace <a> with <Link> */}
            {/* import { Link } from 'react-router-dom'; */}
            {/* <Link to="/aviation/demo" className="..."> */}
            <a
                href="/aviation/demo" // Assumes routing is set up for this path
                className="inline-block px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-800 transition shadow hover:shadow-md transform hover:-translate-y-0.5"
            >
                Launch Simulator Demo
            </a>
           </section>

        </div>
      </main>
       {/* <Footer /> */}
    </div>
  );
};

export default AviationPilotATCUnit1; // Ensure this matches the filename

